import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { TrendingUp, Droplets } from "lucide-react";
import { GiCorn, GiSugarCane } from "react-icons/gi";

/* ===================== THEME / TOKENS ===================== */
const palette = {
  bg: "#f6f9fb",
  cardBg: "#ffffff",
  text: "#0f172a",
  mutedText: "#64748b",
  border: "#e5e7eb",
  primary: "#0ea5e9",
  primarySoft: "rgba(14,165,233,0.12)",
  green: "#16a34a",
  greenSoft: "rgba(22,163,74,0.10)",
  blue: "#2563eb",
  blueSoft: "rgba(37,99,235,0.10)",
  zebra: "#f8fafc",
  shadow: "0 6px 24px rgba(2,8,23,0.06)",
};

/* ===================== LAYOUT ===================== */
const Page = styled.div`
  min-height: 100vh;
  background-color: #eee;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const Grid12 = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* ===================== CARD / BADGE ===================== */
const Card = styled.div`
  position: relative;
  background: ${(p) => p.$bg || palette.cardBg};
  border: 1px solid ${(p) => p.$border || palette.border};
  border-radius: 16px;
  box-shadow: ${palette.shadow};
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  height: 100%;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(2, 8, 23, 0.08);
    border-color: #cfe5ff66;
  }
`;

const CardHeader = styled.div`
  padding: 16px 16px 8px 16px;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  color: ${palette.text};
`;

const IconWrap = styled.div`
  padding: 12px;
  border-radius: 14px;
  background: ${(p) => p.$bg || palette.primarySoft};
  box-shadow: 0 6px 18px rgba(2, 8, 23, 0.06);
  display: grid;
  place-items: center;
`;

const CardContent = styled.div`
  padding: 16px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 1px;
  border: 1px solid ${palette.border};
  background: ${(p) => p.$bg || palette.primarySoft};
  border-radius: 12px;
  font-weight: 600;
  color: ${palette.text};
  user-select: none;
`;

/* ===================== FORM / INPUT ===================== */
const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const InputsGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${palette.mutedText};
`;

const InputBox = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  background: #ffffffcc;
  border: 1px solid ${palette.border};
  border-radius: 12px;
  padding: 10px 12px;

  &:focus-within {
    border-color: ${palette.primary};
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.12);
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    font-weight: 600;
    color: ${palette.text};
    background: transparent;
  }

  small {
    font-size: 12px;
    color: ${palette.mutedText};
    font-weight: 600;
    white-space: nowrap;
  }
`;

/* ===================== SECTIONS ===================== */
const CalcBox = styled.div`
  background: linear-gradient(90deg, #f3f6fb 0%, #eef5ff 100%);
  border: 1px solid #e6eefc;
  border-radius: 14px;
  padding: 12px;
`;

const CalcHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: ${palette.text};
  margin-bottom: 10px;
`;

const ThreeCols = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  .center {
    text-align: center;
  }

  .caption {
    margin-top: 4px;
    font-size: 11px;
    color: ${palette.mutedText};
  }
`;

const FinalResultWrap = styled.div`
  position: relative;
  border-radius: 14px;
  border: 1px solid rgba(22, 163, 74, 0.25);
  background: linear-gradient(90deg, ${palette.greenSoft}, rgba(22, 163, 74, 0.06));
  padding: 10px 18px;
  overflow: hidden;

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .label {
    font-size: 16px;
    font-weight: 700;
    color: ${palette.text};
  }

  .value {
    font-size: 28px;
    font-weight: 800;
    color :${palette.green};
  }
`;

const SectionNote = styled.p`
  margin: 8px 0 0 0;
  font-size: 11px;
  color: ${palette.mutedText};
  text-align: center;
`;
const MetaLine = styled.p`
  margin: 6px 0 0;
  font-size: 11px;
  color: ${palette.mutedText};
`;
const ItemsGrid3 = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;





/* ===================== HELPERS ===================== */
function toNumber(v) {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v.replace(",", "."));
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function formatCurrency(value, currency = "BRL") {
  if (currency === "BRL") {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

/* ===================== MAIN COMPONENT ===================== */
export default function MarketQuotations() {
  const urlApi = process.env.REACT_APP_APIGESTAO_URL;
  // Açúcar VHP (inputs)
  const [quotationCents, setQuotationCents] = useState(0);
  const [tradingDiscount, setTradingDiscount] = useState(-0.08);
  const [dollarRate, setDollarRate] = useState(0);

  // Outros produtos
  const [ethanolAnhydrous, setEthanolAnhydrous] = useState(0);
  const [ethanolHydrated, setEthanolHydrated] = useState(0);
  const [soybeanPrice, setSoybeanPrice] = useState(0);
  const [cornPrice, setCornPrice] = useState(0);
  const [sugarcanePrice, setSugarcanePrice] = useState(0);

  // Derivados Açúcar VHP
  const [netQuotationCents, setNetQuotationCents] = useState(0);
  const [netQuotationUSD, setNetQuotationUSD] = useState(0);
  const [polarizationPremium, setPolarizationPremium] = useState(0);
  const [finalResultBRL, setFinalResultBRL] = useState(0);
  const [finalResultBRLEtanol, setFinalResultBRLEtanol] = useState(0);


  // Datas dos produtos
  const [soybeanDate, setSoybeanDate] = useState("");
  const [cornDate, setCornDate] = useState("");
  const [sugarcaneDate, setSugarcaneDate] = useState("");
  const [anhydrousDate, setAnhydrousDate] = useState("");
  const [hydratedDate, setHydratedDate] = useState("");
  const [sugarDate, setSugarDate] = useState("");
  const [dollarDate, setDollarDate] = useState("");

  // Cálculos
  useEffect(() => {
    const netCents = Number(quotationCents) + Number(tradingDiscount);
    const netUSD = netCents * 22.462;
    const premium = netUSD * 1.042;
    const finalBRL = premium * Number(dollarRate);
    const finalEtanol = finalBRL * (50 / 33);

    setNetQuotationCents(netCents);
    setNetQuotationUSD(netUSD);
    setPolarizationPremium(premium);
    setFinalResultBRL(finalBRL);
    setFinalResultBRLEtanol(finalEtanol);

  }, [quotationCents, tradingDiscount, dollarRate]);

  // ===== Fetchs =====

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/soja");
        const data = await r.json();
        if (data?.valor) {
          const v = parseFloat(String(data.valor).replace(",", "."));
          if (!isNaN(v)) setSoybeanPrice(v);
        }
        if (data?.data) setSoybeanDate(String(data.data));
      } catch (e) {
        console.error("Erro soja:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/milho", { headers: { Accept: "application/json" } });
        const data = await r.json();

        if (data?.valor) {
          const v = parseFloat(String(data.valor).replace(",", "."));
          if (!isNaN(v)) setCornPrice(v);
        }
        if (data?.data) setCornDate(String(data.data));
      } catch (e) {
        console.error("Erro milho:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/cana");
        const data = await r.json();

        if (data?.valor) {
          const v = parseFloat(String(data.valor).replace(",", "."));
          if (!isNaN(v)) setSugarcanePrice(v);
        }
        if (data?.data) setSugarcaneDate(String(data.data));
      } catch (e) {
        console.error("Erro cana:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/etanol-anidro");
        const data = await r.json();

        if (data?.valor) {
          const v = parseFloat(String(data.valor).replace(",", "."));
          if (!isNaN(v)) setEthanolAnhydrous(v);
        }
        if (data?.data) setAnhydrousDate(String(data.data));
      } catch (e) {
        console.error("Erro etanol anidro:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/etanol-hidratado");
        const data = await r.json();

        if (data?.valor) {
          const v = parseFloat(String(data.valor).replace(",", "."));
          if (!isNaN(v)) setEthanolHydrated(v);
        }
        if (data?.data) setHydratedDate(String(data.data));
      } catch (e) {
        console.error("Erro etanol hidratado:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/acucar");
        const data = await r.json();

        if (data?.ajuste) {
          const v = parseFloat(String(data.ajuste).replace(",", "."));
          if (!isNaN(v)) setQuotationCents(v);
        }
        if (data?.data) setSugarDate(String(data.data));
      } catch (e) {
        console.error("Erro açúcar:", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api-gestao/cotacao/dolar");
        const data = await r.json();

        const venda = toNumber(data?.venda);
        if (venda != null) setDollarRate(venda);
        if (data?.dataHoraCotacao) setDollarDate(String(data.dataHoraCotacao));
      } catch (e) {
        console.error("Erro dólar:", e);
      }
    })();
  }, []);

  function formatPTAXStamp(s, withSeconds = false) {
    if (!s) return "—";
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/
    );
    if (!m) return s; // fallback se vier fora do padrão

    const [, yyyy, mm, dd, HH, MM, SS] = m;
    return withSeconds
      ? `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS || "00"}`
      : `${dd}/${mm}/${yyyy} ${HH}:${MM}`;
  }

  // Componente de entrada numérica (equivalente ao NumericInput)
  const NumericInput = ({ label, value, onChange, unit, step = 0.01 }) => (
    <Field>
      <Label>{label}</Label>
      <InputBox>
        <input
          type="number"
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        />
        <small>{unit}</small>
      </InputBox>
    </Field>
  );

  return (
    <Page>
      <Container>
        <Grid12>
          {/* Col principal: 7/12 (desktop) */}
          <div style={{ gridColumn: "span 12" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: 12,
              }}
            >
              <div style={{ gridColumn: "span 12", order: 1 }}>
                <Card
                  $bg="linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(16,185,129,0.04) 100%)"
                  $border="rgba(34,197,94,0.25)"
                >
                  <CardHeader>
                    <CardTitle>
                      <IconWrap $bg="linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.1))">
                        <GiSugarCane size={28} color={palette.green} />
                      </IconWrap>
                      <span
                        style={{
                          backgroundImage:
                            "linear-gradient(90deg, #16a34a 0%, #10b981 100%)",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        Açúcar VHP
                      </span>
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    {/* Inputs */}
                    <InputsGrid>

                      <div>
                        <NumericInput
                          label="Cotação"
                          value={quotationCents}
                          onChange={setQuotationCents}
                          unit="cents/lb"
                          step={0.01}
                        />
                        <MetaLine>Referência: {sugarDate || "—"}</MetaLine>
                        <MetaLine>Fonte: UDOP - Açúcar NY nº11 Futuros</MetaLine>
                      </div>
                      <NumericInput
                        label="Basis"
                        value={tradingDiscount}
                        onChange={setTradingDiscount}
                        unit="cents/lb"
                        step={0.01}
                      />


                      <div>
                        <NumericInput
                          label="Cotação do Dólar"
                          value={dollarRate}
                          onChange={setDollarRate}
                          unit="R$"
                          step={0.0001}
                        />
                        <MetaLine>Referência: {dollarDate ? formatPTAXStamp(dollarDate) : "—"}</MetaLine>
                        <MetaLine>Fonte: Dólar PTAX</MetaLine>
                      </div>
                    </InputsGrid>

                    {/* Cálculos */}
                    <CalcBox style={{ marginTop: 14 }}>
                      <CalcHeader>
                        <TrendingUp size={16} color={palette.green} />
                        Cálculos Automáticos
                      </CalcHeader>

                      <ThreeCols>
                        <div className="center">
                          <Badge $bg="linear-gradient(90deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))">
                            {netQuotationCents.toFixed(2)} cents
                          </Badge>
                          <div className="caption">Cotação Líquida</div>
                        </div>

                        <div className="center">
                          <Badge $bg="linear-gradient(90deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))">
                            {formatCurrency(netQuotationUSD, "USD")}
                          </Badge>
                          <div className="caption">USD/ton sem Pol</div>
                        </div>

                        <div className="center">
                          <Badge $bg="linear-gradient(90deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))">
                            {formatCurrency(polarizationPremium, "USD")}
                          </Badge>
                          <div className="caption">USD/ton com Pol</div>
                        </div>
                      </ThreeCols>
                    </CalcBox>

                    {/* Resultado Final */}
                    <div style={{ marginTop: 14 }}>
                      <FinalResultWrap>
                        <div className="row">
                          <span className="label">Açúcar VHP (R$/t):</span>
                          <span className="value">{formatCurrency(finalResultBRL)}</span>
                        </div>
                      </FinalResultWrap>
                    </div>
                    <div style={{ marginTop: 14 }}>
                      <FinalResultWrap>
                        <div className="row">
                          <span className="label">Paridade do Etanol Equivalente (R$/m³):</span>
                          <span className="value">{formatCurrency(finalResultBRLEtanol)}</span>
                        </div>
                      </FinalResultWrap>
                    </div>


                  </CardContent>
                </Card>
              </div>

              {/* Col lateral: 5/12 (desktop) => empilhado no mobile */}
              <div style={{ gridColumn: "span 12", order: 2 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 12,
                  }}
                >
                  {/* Etanol */}
                  <Card
                    $bg="linear-gradient(135deg, rgba(37,99,235,0.06), rgba(59,130,246,0.04))"
                    $border="rgba(59,130,246,0.25)"
                  >
                    <CardContent>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          fontSize: 20,
                          marginBottom: 12,
                          fontWeight: 700,
                          color: "#1d4ed8",
                        }}
                      >
                        <IconWrap $bg="linear-gradient(135deg, rgba(191,219,254,0.8), rgba(219,234,254,0.7))">
                          <Droplets size={28} color="#2563eb" />
                        </IconWrap>
                        <span>Etanol</span>
                      </div>

                      <InputsGrid2>


                        <div>
                          <NumericInput
                            label="Anidro"
                            value={ethanolAnhydrous}
                            onChange={setEthanolAnhydrous}
                            unit="R$/L"
                            step={0.0001}
                          />
                          <MetaLine>Referência: {anhydrousDate || "—"}</MetaLine>

                        </div>
                        <div>
                          <NumericInput
                            label="Hidratado"
                            value={ethanolHydrated}
                            onChange={setEthanolHydrated}
                            unit="R$/L"
                            step={0.01}
                          />
                          <MetaLine>Referência: {hydratedDate || "—"}</MetaLine>

                        </div>
                      </InputsGrid2>

                      <SectionNote>Fonte: CEPEA/ESALQ</SectionNote>
                    </CardContent>
                  </Card>

                  {/* Grãos e Cana */}
                  <Card
                    $bg="linear-gradient(135deg, rgba(34,197,94,0.06), rgba(16,185,129,0.04))"
                    $border="rgba(34,197,94,0.25)"
                  >
                    <CardContent>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          fontSize: 20,
                          marginBottom: 12,
                          fontWeight: 700,
                          color: "#15803d",
                        }}
                      >
                        <IconWrap $bg="linear-gradient(135deg, rgba(220,252,231,0.9), rgba(240,253,244,0.8))">
                          <GiCorn size={28} color="#16a34a" />
                        </IconWrap>
                        <span>Grãos e Cana</span>
                      </div>

                      <ItemsGrid3>
                        {/* Cana */}
                        <div>
                          <NumericInput
                            label="Cana"
                            value={sugarcanePrice}
                            onChange={setSugarcanePrice}
                            unit="R$/t"
                            step={0.01} />
                          <MetaLine>Referência: {sugarcaneDate || "—"}</MetaLine>
                          <MetaLine>Fonte: UDOP</MetaLine>
                        </div>

                        {/* Milho */}
                        <div>
                          <NumericInput label="Milho"
                            value={cornPrice}
                            onChange={setCornPrice}
                            unit="R$/saca"
                            step={0.01} />
                          <MetaLine>Referência: {cornDate || "—"}</MetaLine>
                          <MetaLine>Fonte: CEPEA/ESALQ</MetaLine>
                        </div>

                        {/* Soja */}
                        <div>
                          <NumericInput
                            label="Soja"
                            value={soybeanPrice}
                            onChange={setSoybeanPrice}
                            unit="R$/saca"
                            step={0.01} />
                          <MetaLine>Referência: {soybeanDate || "—"}</MetaLine>
                          <MetaLine>Fonte: CEPEA/ESALQ</MetaLine>
                        </div>
                      </ItemsGrid3>

                      {/* <SectionNote>Fonte: CEPEA/ESALQ</SectionNote> */}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </Grid12>
      </Container>
    </Page>
  );
}
